import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import Col from "react-bootstrap/esm/Col";
import Row from "react-bootstrap/Row";
import Button from "react-bootstrap/Button";
import { useTranslation } from "react-i18next";
import { InputText, InputSelect, InputNumber } from "@2solve/tosolve-inputs";
import useFormValidation from "../../../hooks/useFormValidation";
import handleInputChange from "../../../hooks/inputChange";

function MapeamentoModal({
  show,
  handleClose,
  error,
  setError,
  mappingId,
  setMappingId,
  history,
  setHistory,
  input,
  setCodecs,
  handleDataAdded,
  postData,
  strategy,
  codecs,
}) {
  const { t } = useTranslation();
  const isButtonDisabled = useFormValidation(); // Usando o Hook
  const data_typeRef = useRef(null);
  const inputRef = useRef(null);
  const [formData, setFormData] = useState({
    input_id: null,
    data_type: null,
    input_code: null,
    byte_length: null,
    codec_id: null,
    decoding_strategy_id: null,
    name: "",
    description: "",
  });

  const typeOptions = [
    { value: "uint", label: "uint" },
    { value: "int", label: "int" },
    { value: "float", label: "float" },
    { value: "string", label: "string" },
    { value: "hex", label: "hex" },
    { value: "bool", label: "bool" },
  ];

  useEffect(() => {
    if (show) {
      setFormData({
        input_id: null,
        data_type: null,
        input_code: null,
        byte_length: null,
        codec_id: null,
        name: "",
        description: "",
        decoding_strategy_id: null,
      });
    }
  }, [show]);

  const handleCreate = (event) => {
    event.preventDefault();

    if (formData.name.length > 40) {
      setError(t("O nome deve ter no máximo 40 caracteres."));
      return;
    } else {
      setError("");
    }

    if (!formData.name) {
      setError(t("O campo Nome do mapeamento é obrigatório."));
      return;
    }

    if (!formData.input_id) {
      setError(t("O campo Input é obrigatório."));
      return;
    }

    if (!formData.data_type) {
      setError(t("O campo Tipo do dado é obrigatório."));
      return;
    }

    // Apenas verifica duplicação de input se um input foi selecionado
    const existingInput = formData.input_id
      ? history.find(
          (entry) =>
            entry.name &&
            entry.name.trim() === formData.name.trim() &&
            entry.input &&
            entry.input.id === formData.input_id,
        )
      : null;

    if (existingInput) {
      setError(t("Esse input já está cadastrado"));
      return;
    }
    const newEntry = {
      id: mappingId,
      name: formData.name,
      description: formData.description,
      input: formData.input_id
        ? input.find((item) => item.id === formData.input_id)
        : null,
      data_type: formData.data_type,
      input_code: formData.input_code,
      byte_length: formData.byte_length,
      codec_id: formData.codec_id,
      decoding_strategy_id: formData.decoding_strategy_id,
    };
    setHistory([...history, { ...newEntry }]);
    setError("");

    setFormData({
      ...formData,
      data_type: null,
      input_code: null,
      byte_length: null,
      input_id: null,
      data_type: null,
    });
    inputRef.current?.clearValue();
    data_typeRef.current?.clearValue();
  };

  const handleAdd = async () => {
    const firstPostData = {
      name: formData.name,
      description: formData.description,
      decoding_strategy_id: formData.decoding_strategy_id,
    };

    try {
      // Primeira requisição para criar o grupo de mapeamento
      const response = await postData("/codecs", firstPostData);
      const returnedMappingId = response.data.id;
      const returnedMappingName = response.data.name;
      const returnedMappingDescription = response.data.description;
      const returnedMappingDecodingStrategyId =
        response.data.decoding_strategy_id;
      setMappingId(returnedMappingId);
      setCodecs([
        ...codecs,
        {
          id: returnedMappingId,
          name: returnedMappingName,
          description: returnedMappingDescription,
          decoding_strategy_id: returnedMappingDecodingStrategyId,
        },
      ]);

      // Construir o array de mapeamentos
      if (history.length > 0) {
        const mappings = history.map((entry) => ({
          input_id: entry.input ? entry.input.id : null,
          data_type: entry.data_type,
          input_code: entry.input_code,
          byte_length: entry.byte_length,
          codec_id: returnedMappingId,
        }));

        // Enviar todos os mapeamentos em uma única requisição
        const bulkPostResponse = await postData("/codec-inputs", mappings);

        const newEntries = bulkPostResponse.data; // Supondo que o endpoint retorne os objetos criados

        setHistory([]); // Limpar o histórico após salvar
        handleDataAdded(newEntries); // Atualizar os dados adicionados
      }
      setError("");
      handleClose(); // Fechar modal
    } catch (error) {
      if (error.status) {
        if (error.status === 400) {
          setError(
            t(
              "O nome pode conter apenas letras, números, underscore, hífen e espaço",
            ),
          );
        }
        if (error.status === 409) {
          setError(t("Já existe um mapeamento com esse nome"));
        }
      } else {
        console.error("Erro desconhecido:", error);
      }
    }
  };

  const handleDelete = (index) => {
    setHistory((prevHistory) => prevHistory.filter((_, i) => i !== index));
  };

  const inputOptions = input.map((i) => ({
    value: i.id,
    label: i.name,
  }));
  const strategyOptions = strategy.map((i) => ({
    value: i.id,
    label: i.name,
  }));

  const closeModal = () => {
    setFormData({
      input_id: null,
      data_type: null,
      input_code: null,
      byte_length: null,
      codec_id: null,
      decoding_strategy_id: null,
      name: "",
      description: "",
    });
    setError("");
    handleClose();
  };
  return (
    <Modal backdrop="static" size="xl" centered show={show} onHide={closeModal}>
      <Modal.Header closeButton closeVariant="white">
        <Modal.Title>{t("Criar Codec")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleCreate}>
          <InputText
            id="name"
            type="text"
            placeholder={t("Nome")}
            required={true}
            maxLength={40}
            value={formData?.name}
            onChange={handleInputChange(setFormData)}
          />
          <InputText
            id="description"
            className="mt-4"
            type="text"
            placeholder={t("Descrição")}
            required={true}
            value={formData?.description}
            onChange={handleInputChange(setFormData)}
          />

          <InputSelect
            className="mt-4"
            placeholder={t("Estratégia de decodificação")}
            id="decoding_strategy_id"
            type="select"
            required={true}
            options={strategyOptions}
            isMultiple={false}
            selectAll={false}
            closeOnSelect={true}
            IsClearable={false}
            disabled={false}
            onChange={
              handleInputChange(setFormData) // Atualiza o formData
            }
            value={formData.decoding_strategy_id || ""} // Valor controlado pelo estado
          />
          <Row>
            <Col>
              <InputSelect
                ref={inputRef}
                placeholder={t("Input *")}
                id="input_id"
                type="select"
                options={inputOptions}
                isMultiple={false}
                selectAll={false}
                closeOnSelect={true}
                IsClearable={true}
                required={false}
                disabled={false}
                onChange={handleInputChange(setFormData)}
                value={formData.input_id || ""} // Valor controlado pelo estado
              />
            </Col>
            <Col>
              <InputSelect
                ref={data_typeRef}
                placeholder={t("Tipo do dado *")}
                id="data_type"
                type="select"
                options={typeOptions}
                isMultiple={false}
                selectAll={false}
                closeOnSelect={true}
                IsClearable={true}
                disabled={false}
                required={false}
                onChange={handleInputChange(setFormData)}
                value={formData.data_type || ""} // Valor controlado pelo estado
              />
            </Col>
          </Row>
          <Row>
            <Col>
              <InputNumber
                id="input_code"
                type="int"
                placeholder={t("Codigo de input")}
                required={true}
                value={formData?.input_code || ""}
                onChange={handleInputChange(setFormData)}
              />
            </Col>
            <Col>
              {" "}
              <InputNumber
                required={true}
                id="byte_length"
                type="int"
                placeholder={t("Tamanho em bytes")}
                value={formData?.byte_length || ""}
                onChange={handleInputChange(setFormData)}
              />
            </Col>
          </Row>
          <div className="d-flex justify-content-end">
            <Button disabled={isButtonDisabled} variant="primary" type="submit">
              {t("button_add")}
            </Button>
          </div>
          {error && <div className="text-danger">{error}</div>}
        </Form>
        <div>
          {history.length > 0 && (
            <table className="table">
              <thead>
                <tr>
                  <th>{t("input")}</th>
                  <th>{t("data_type")}</th>
                  <th>{t("input_code")}</th>
                  <th>{t("byte_length")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {history.map((entry, index) => (
                  <tr key={index}>
                    <td>{entry.input ? entry.input.name : "-"}</td>
                    <td>{entry.data_type ? entry.data_type : "-"}</td>
                    <td>{entry.input_code ? entry.input_code : "-"}</td>
                    <td>{entry.byte_length ? entry.byte_length : "-"}</td>
                    <td className="text-end">
                      <Button
                        variant="danger"
                        onClick={() => handleDelete(index)}
                      >
                        {t("button_delete")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={handleAdd}>
          {t("Cadastrar")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

MapeamentoModal.propTypes = {
  show: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  error: PropTypes.string,
  setError: PropTypes.func.isRequired,
  mappingId: PropTypes.string,
  setMappingId: PropTypes.func.isRequired,
  history: PropTypes.array.isRequired,
  setHistory: PropTypes.func.isRequired,
  strategy: PropTypes.array.isRequired,
  input: PropTypes.array.isRequired,
  setCodecs: PropTypes.func.isRequired,
  mappingGroup: PropTypes.array.isRequired,
  handleDataAdded: PropTypes.func.isRequired,
  postData: PropTypes.func.isRequired,
};

export default MapeamentoModal;
